import { Core } from "@faire/design-tokens";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Button } from "@faire/web/slate/Button";
import { Column, Flex, Row } from "@faire/web/slate/Layout";
import { getSpacing, SpacerMultipliersKey } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import { Typography } from "@faire/web/slate/Typography";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React from "react";
import styled, { css, keyframes } from "styled-components";

import { Context } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Context";
import { PAGE_TYPE } from "@faire/retailer/consts/PAGE_TYPE";
import { ShowroomVettingCriteria } from "@faire/retailer/features/Showroom/ShowroomVettingCriteria/ShowroomVettingCriteria";

const IMAGE_DELAY_DURATION = 3000;
const IMAGE_DISSOLVE_DURATION = 300;

const getFadeInPercent = (totalDuration: number) =>
  `${(100 * IMAGE_DISSOLVE_DURATION) / totalDuration}%`;

const getDelayPercent = (totalDuration: number) =>
  `${
    (100 * (IMAGE_DELAY_DURATION + IMAGE_DISSOLVE_DURATION)) / totalDuration
  }%`;

const getFadeOutPercent = (totalDuration: number) =>
  `${
    (100 * (IMAGE_DELAY_DURATION + 2 * IMAGE_DISSOLVE_DURATION)) / totalDuration
  }%`;

export const ShowroomHeaderBanner: React.FC = () => {
  const { layoutElement, onCTAClick, pageType, requestId } =
    React.useContext(Context);
  const {
    main_text,
    cta_link,
    cta_text,
    images,
    on_click_event_name,
    description_text,
  } = layoutElement;
  const isSSR = useIsSSR();

  if (!main_text || !cta_link || !cta_text) {
    return null;
  }

  const isHomepage = pageType === PAGE_TYPE.HOMEPAGE;
  const numCategory = images.length / 2;
  const totalDuration =
    (IMAGE_DELAY_DURATION + IMAGE_DISSOLVE_DURATION) * numCategory;
  const bannerInfo = on_click_event_name ?? "ShowroomHeaderBanner";

  const gap = (): ResponsiveValues<SpacerMultipliersKey> => {
    return {
      mobileAndAbove: "0",
      tabletAndAbove: "7x",
      desktopAndAbove: "3x",
      xLargeAndAbove: isHomepage ? "3x" : "4x",
    };
  };

  const showroomVettingCriteria = (showBorder: boolean) => (
    <ShowroomVettingCriteria
      gap={gap()}
      color={Core.text.inverse}
      border={showBorder}
      column={showBorder}
      alignText={showBorder ? "center" : "left"}
    />
  );

  return (
    <Container data-test-id="showroom-header-banner">
      <LeftContentWrapper $isHomepage={isHomepage}>
        <TextAndButtonWrapper $isHomepage={isHomepage}>
          <Typography color={Core.text.inverse} variant="paragraphSansRegular">
            {description_text}
          </Typography>
          <StyledMainText
            variant={{
              desktopAndAbove: "displayLSerifRegular",
              mobileAndAbove: "displayMSerifRegular",
            }}
          >
            {main_text}
          </StyledMainText>
          <StyledButton
            linkTo={cta_link}
            variant="secondary"
            onClick={() => {
              onCTAClick?.(bannerInfo, requestId);
            }}
          >
            {cta_text}
          </StyledButton>
        </TextAndButtonWrapper>
        <ShowroomVettingCriteriaWrapper $isHomepage={isHomepage}>
          <ResponsiveContentV2
            mobile={showroomVettingCriteria(false)}
            tabletAndAbove={showroomVettingCriteria(true)}
            xLargeAndAbove={
              isHomepage
                ? showroomVettingCriteria(true)
                : showroomVettingCriteria(false)
            }
          />
        </ShowroomVettingCriteriaWrapper>
      </LeftContentWrapper>
      <ImageContainer>
        {images.map((image, index) => {
          return (
            <StyledImage
              $left={index % 2 ? "50%" : "0"}
              $order={Math.floor(index / 2)}
              $animated={numCategory > 1}
              $fadeInPercent={getFadeInPercent(totalDuration)}
              $fadeOutPercent={getFadeOutPercent(totalDuration)}
              $delayPercent={getDelayPercent(totalDuration)}
              $delay={IMAGE_DELAY_DURATION}
              $dissolve={IMAGE_DISSOLVE_DURATION}
              $total={totalDuration}
              key={`showroom-normal-category-header-banner-${index}`}
              src={optimizeImageUrl(image, isSSR)}
            />
          );
        })}
      </ImageContainer>
    </Container>
  );
};

const Container = styled(Flex).attrs({
  direction: { mobileAndAbove: "column", desktopAndAbove: "row" },
  justify: { mobileAndAbove: "normal", xLargeAndAbove: "flex-end" },
})`
  position: relative;
  background: ${Core.surface.enabled};
  width: 100%;
  overflow: hidden;
  z-index: 1;
`;

const LeftContentWrapper = styled(Column).attrs({
  align: { mobileAndAbove: "center", desktopAndAbove: "flex-start" },
})<{
  $isHomepage?: boolean;
}>`
  width: 100%;
  height: fit-content;
  padding: ${getSpacing("7x", 0, "6x")};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding-bottom: ${getSpacing("3x")};
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    width: 50%;
    padding-top: ${(props) => getSpacing(props.$isHomepage ? "7x" : "9x")};
  }

  ${responsiveMediaQueries.xLargeAndAbove} {
    padding-top: ${(props) => getSpacing(props.$isHomepage ? "7x" : "10x")};
    max-width: 640px;
  }
`;

const TextAndButtonWrapper = styled(Column).attrs({
  align: { mobileAndAbove: "center", desktopAndAbove: "flex-start" },
})<{
  $isHomepage?: boolean;
}>`
  ${responsiveMediaQueries.desktopAndAbove} {
    margin: 0 auto;
  }

  ${responsiveMediaQueries.xLargeAndAbove} {
    padding-left: ${(props) => getSpacing(props.$isHomepage ? "5x" : 0)};
    margin: 0;
  }
`;

const ImageContainer = styled(Row)`
  width: 100%;
  padding-top: 75%;
  position: relative;
  overflow: hidden;

  ${responsiveMediaQueries.desktopAndAbove} {
    width: 50%;
    padding: 0;
  }
`;

const fade = (fadeIn: string, delay: string, fadeOut: string) =>
  keyframes`
  0% { opacity: 0; };
  ${fadeIn} {
    opacity: 1;
  }
  ${delay} {
    opacity: 1;
  }
  ${fadeOut} {
    opacity: 0;
  }
  100% { opacity: 0; }
`;

const StyledImage = styled.img<{
  $left: string;
  $order: number;
  $animated?: boolean;
  $fadeInPercent: string;
  $delayPercent: string;
  $fadeOutPercent: string;
  $delay: number;
  $dissolve: number;
  $total: number;
}>`
  position: absolute;
  width: 50%;
  top: 0;
  left: ${(props) => props.$left};
  height: auto;
  min-height: 100%;
  object-fit: cover;
  ${(props) =>
    props.$animated
      ? css`
          animation: ${fade(
              props.$fadeInPercent,
              props.$delayPercent,
              props.$fadeOutPercent
            )}
            ${props.$total}ms ease-out
            ${props.$order * (props.$delay + props.$dissolve)}ms infinite;
          opacity: 0;
        `
      : ""};
`;

const StyledMainText = styled(Typography).attrs({
  color: Core.text.inverse,
})`
  &&& {
    padding: ${getSpacing(0, "1x", "3x")};
    text-align: center;
    max-width: 464px;

    ${responsiveMediaQueries.tabletAndAbove} {
      padding: ${getSpacing(0, 0, "4x")};
    }

    ${responsiveMediaQueries.desktopAndAbove} {
      text-align: start;
    }
  }
`;

const StyledButton = styled(Button)`
  &&& {
    padding: ${getSpacing("2x")};
    width: 200px;
    &:active,
    &:focus,
    &:hover {
      background-color: ${Core.surface.inverse};
      color: ${Core.text.default};
    }
  }
`;

const ShowroomVettingCriteriaWrapper = styled(Flex).attrs({
  align: "center",
  direction: {
    mobileAndAbove: "column",
    tabletAndAbove: "row",
  },
})<{ $isHomepage?: boolean }>`
  margin: auto;
  padding-top: ${getSpacing("6x")};
  row-gap: ${getSpacing("3x")};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing("8x", 0, 0, "6x")};
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    padding-left: 0;
    padding-top: ${(props) => getSpacing(props.$isHomepage ? "5x" : "8x")};
  }

  ${responsiveMediaQueries.xLargeAndAbove} {
    padding-top: ${(props) => getSpacing(props.$isHomepage ? "5x" : "10x")};
    margin-left: ${(props) => getSpacing(props.$isHomepage ? "2x" : "-4x")};
  }
`;
